import { useQuery } from '@tanstack/react-query';
import { must, useDb } from './db';

/**
 * «Питомець класу»: grows from the whole class's care — handed-in homework, answered tests,
 * lessons attended (rpc class_pet). Stages go by points per student, so a small class grows as
 * fast as a big one. Nobody's share is shown: it is the class's pet.
 */
export const PET_STAGES = [
  { key: 'egg', emoji: '🥚', min: 0 },
  { key: 'hatched', emoji: '🐣', min: 8 },
  { key: 'baby', emoji: '🦎', min: 20 },
  { key: 'young', emoji: '🐲', min: 40 },
  { key: 'dragon', emoji: '🐉', min: 65 },
  { key: 'guardian', emoji: '🐉', min: 95 },
] as const;
export type PetStage = (typeof PET_STAGES)[number];

export function petStage(points: number, students: number) {
  const perStudent = students > 0 ? points / students : 0;
  let index = 0;
  PET_STAGES.forEach((s, i) => {
    if (perStudent >= s.min) index = i;
  });
  const stage = PET_STAGES[index] as PetStage;
  const next = PET_STAGES[index + 1];
  return {
    stage,
    next,
    /** 0…1 on the way to the next stage; 1 at the last one */
    progress: next ? (perStudent - stage.min) / (next.min - stage.min) : 1,
  };
}

export interface ClassPet {
  points: number;
  students: number;
  weekPoints: number;
  /** the share of this week's homework handed in, null when none was due */
  weekHomework: number | null;
  weekAttendance: number | null;
  weekTests: number;
}

export function useClassPet(classId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['class-pet', classId],
    enabled: classId !== undefined,
    queryFn: async (): Promise<ClassPet | null> => {
      const row = must(await db.rpc('class_pet', { p_class: classId as string }))[0];
      return row
        ? {
            points: row.points,
            students: row.students,
            weekPoints: row.week_points,
            weekHomework:
              row.week_homework_due > 0 ? row.week_homework_done / row.week_homework_due : null,
            weekAttendance: row.week_attendance,
            weekTests: row.week_tests,
          }
        : null;
    },
  });
}
